import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './App.css';
import AppHeader from './components/layout/AppHeader';
import AppRoutes from './routes/AppRoutes';
import { usePlayerForm } from './hooks/usePlayerForm';
import { useAuth } from './context/AuthContext';
import {
  createPlayerProfile,
  fetchMessage,
  initialForm,
  updatePlayerProfile,
  fetchMyProfile,
} from './services/playerService';
import { normalizeProfile } from './utils/profile';
import { LAST_ROUTE_STORAGE_KEY } from './utils/navigation';

function App() {
  const location = useLocation();
  const { isLoadingAuth, logout, refreshUser, token, user } = useAuth();
  const userName = user?.name;
  const userEmail = user?.email;
  const userPhone = user?.mobile || user?.phone || user?.phoneNumber;

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);
  const {
    createdProfile,
    formData,
    handleChange,
    handleFileChange,
    setCreatedProfile,
    setFormData,
    setStatusMessage,
    statusMessage,
    manageListItem,
    toggleAddOn,
    toggleSkillTag,
    updateAddOnField,
  } = usePlayerForm();
  const [message, setMessage] = useState('');
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [hasExistingProfile, setHasExistingProfile] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (!toastMessage) return undefined;

    const timeoutId = window.setTimeout(() => setToastMessage(''), 3000);
    return () => window.clearTimeout(timeoutId);
  }, [toastMessage]);

  useEffect(() => {
    fetchMessage()
      .then((data) => setMessage(data.message))
      .catch((error) => {});
  }, []);

  const loadMyProfile = useCallback(async () => {
    if (!token) {
      setHasExistingProfile(false);
      setCreatedProfile(null);
      setFormData(initialForm);
      return;
    }

    setIsLoadingProfile(true);
    setStatusMessage('');

    try {
      const profileResponse = await fetchMyProfile(token);
      const profile = profileResponse?.profile || profileResponse || null;
      if (!profile) {
        setHasExistingProfile(false);
        setCreatedProfile(null);
        setFormData(initialForm);
        setStatusMessage('Create your player profile.');
        return;
      }

      const normalized = normalizeProfile(profile);
      setFormData(normalized);
      setCreatedProfile(normalized);
      setHasExistingProfile(true);
      setStatusMessage('Loaded your saved profile.');
    } catch (error) {
      if ((error.message || '').toLowerCase().includes('not found')) {
        setHasExistingProfile(false);
        setCreatedProfile(null);
        setFormData(initialForm);
        setStatusMessage('Create your player profile.');
      } else {
        setStatusMessage(error.message || 'Failed to load your profile.');
      }
    } finally {
      setIsLoadingProfile(false);
    }
  }, [setCreatedProfile, setFormData, setStatusMessage, token]);

  useEffect(() => {
    loadMyProfile();
  }, [loadMyProfile]);

  useEffect(() => {
    if (typeof window === 'undefined' || !token) return undefined;

    const refreshProfileStats = () => {
      loadMyProfile();
    };

    window.addEventListener('match-finalized', refreshProfileStats);
    window.addEventListener('scorecard-updated', refreshProfileStats);

    return () => {
      window.removeEventListener('match-finalized', refreshProfileStats);
      window.removeEventListener('scorecard-updated', refreshProfileStats);
    };
  }, [loadMyProfile, token]);

  useEffect(() => {
    if (!userName && !userEmail && !userPhone) return;

    setFormData((prev) => {
      const nextFullName = userName || prev.personal.fullName;
      const nextEmail = userEmail || prev.personal.email;
      const nextMobile = userPhone || prev.personal.mobile;
      const isUnchanged = prev.personal.fullName === nextFullName
        && prev.personal.email === nextEmail
        && prev.personal.mobile === nextMobile;
      if (isUnchanged) return prev;


      return {
        ...prev,
        personal: {
          ...prev.personal,
          fullName: nextFullName,
          email: nextEmail,
          mobile: nextMobile,
        },
      };
    });
  }, [setFormData, user?.id, userEmail, userName, userPhone]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatusMessage('');

    if (!token) {
      setStatusMessage('Please log in to save or edit your player profile.');
      return false;
    }

    try {
      setIsSavingProfile(true);
      const shouldUpdateExisting = hasExistingProfile;
      const saveResponse = shouldUpdateExisting
        ? await updatePlayerProfile(formData, token)
        : await createPlayerProfile(formData, token);
      const savedProfile = saveResponse?.player || saveResponse;
      const normalizedProfile = normalizeProfile(savedProfile);

      setCreatedProfile(normalizedProfile);
      setHasExistingProfile(true);

      await loadMyProfile();
      await refreshUser();
      setStatusMessage(shouldUpdateExisting ? 'Profile updated successfully.' : 'Profile saved to MongoDB successfully.');
      return true;
    } catch (error) {
      setStatusMessage(error.message || 'Failed to save player profile.');
      return false;
    } finally {
      setIsSavingProfile(false);
    }
  };

  const hideHeader =
    location.pathname.startsWith('/auth') ||
    location.pathname.startsWith('/login') ||
    location.pathname.startsWith('/super-admin') ||
    location.pathname.startsWith('/sa');

  const isSuperAdminWorkspace = location.pathname !== '/sa/login'
    && (location.pathname === '/sa' || location.pathname.startsWith('/sa/'));


  const showHeroHeader = ['/', '/home', '/dashboard/individual/home'].includes(location.pathname);


  useEffect(() => {
    // Persist route state for authenticated app pages, excluding auth entry routes only.
    const disallowedPaths = ['/auth', '/login'];
    const shouldPersistPath = !disallowedPaths.some((path) => location.pathname.startsWith(path));

    if (shouldPersistPath) {
      const currentPath = `${location.pathname}${location.search}${location.hash}`;
      sessionStorage.setItem(LAST_ROUTE_STORAGE_KEY, currentPath);
    }
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    const scrollKey = `scroll-position:${location.pathname}${location.search}${location.hash}`;

    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    const savedScrollPosition = sessionStorage.getItem(scrollKey);
    if (savedScrollPosition) {
      const scrollY = Number(savedScrollPosition);
      if (!Number.isNaN(scrollY)) {
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });
      }
    }

    const saveScrollPosition = () => {
      sessionStorage.setItem(scrollKey, String(window.scrollY));
    };

    window.addEventListener('pagehide', saveScrollPosition);
    window.addEventListener('beforeunload', saveScrollPosition);

    return () => {
      saveScrollPosition();
      window.removeEventListener('pagehide', saveScrollPosition);
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, [location.hash, location.pathname, location.search]);

  return (
    <div className="App">
      <AppHeader
        hideHeader={hideHeader}
        isLoadingAuth={isLoadingAuth}
        message={message}
        showHero={showHeroHeader}
        user={user}
        logout={handleLogout}
      />

      <main className={isSuperAdminWorkspace ? 'app-shell app-shell--super-admin' : 'container py-5 app-shell'}>
        <AppRoutes
          createdProfile={createdProfile}
          formData={formData}
          handleChange={handleChange}
          handleFileChange={handleFileChange}
          handleSubmit={handleSubmit}
          isLoadingProfile={isLoadingProfile}
          isSavingProfile={isSavingProfile}
          onRefreshProfile={loadMyProfile}
          statusMessage={statusMessage}
          manageListItem={manageListItem}
          toggleAddOn={toggleAddOn}
          toggleSkillTag={toggleSkillTag}
          updateAddOnField={updateAddOnField}
        />
      </main>

      {toastMessage && (
        <div className="toast show position-fixed bottom-0 end-0 m-3 align-items-center text-bg-primary border-0" role="status" aria-live="polite">
          <div className="d-flex">
            <div className="toast-body">{toastMessage}</div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
